import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { mergeSiteSettings, SITE_SETTINGS_DEFAULTS } from './siteSettingsDefaults';
import { applyJewelryProfilesFromSettings } from './jewelry/jewelryProfileRegistry';
import { setStoreOptionLists } from './jewelry/jewelryStoreOptionLists';

const SiteSettingsContext = createContext({
  settings: SITE_SETTINGS_DEFAULTS,
  status: 'loading',
  refresh: () => {},
});

export { SiteSettingsContext };

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(SITE_SETTINGS_DEFAULTS);
  const [status, setStatus] = useState('loading');

  const load = React.useCallback(async () => {
    setStatus('loading');
    try {
      const r = await fetch(`/api/settings.php?_=${Date.now()}`, { cache: 'no-store' });
      const data = await r.json().catch(() => ({}));
      if (data.ok && data.settings) {
        const merged = mergeSiteSettings(data.settings);
        applyJewelryProfilesFromSettings(merged);
        const jewelryConfig = merged?.jewelry_profiles_config;
        const storeListsRaw =
          jewelryConfig?.storeOptionLists && typeof jewelryConfig.storeOptionLists === 'object'
            ? { storeOptionLists: jewelryConfig.storeOptionLists }
            : jewelryConfig?.storeOptionDefaults && typeof jewelryConfig.storeOptionDefaults === 'object'
              ? jewelryConfig.storeOptionDefaults
              : null;
        setStoreOptionLists(storeListsRaw);
        setSettings(merged);
        setStatus('ready');
      } else {
        setSettings(SITE_SETTINGS_DEFAULTS);
        setStatus('error');
      }
    } catch {
      setSettings(SITE_SETTINGS_DEFAULTS);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(
    () => ({ settings, status, refresh: load }),
    [settings, status, load],
  );

  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}
